import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "feature" | "compact";
  index?: number;
}

function MetaRow({ article }: { article: Article }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
      <span className="flex items-center gap-1">
        <i className="ri-calendar-line text-sm text-accent-600" />
        {article.date}
      </span>
      <span className="flex items-center gap-1">
        <i className="ri-time-line text-sm text-accent-600" />
        {article.readTime}
      </span>
      <span className="flex items-center gap-1">
        <i className="ri-fire-line text-sm text-primary-400" />
        {article.heat.toLocaleString("zh-CN")}
      </span>
    </div>
  );
}

export default function ArticleCard({ article, variant = "default", index = 0 }: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex items-start gap-4 border-b border-background-200/70 py-4 last:border-b-0 cursor-pointer"
      >
        <span className="mt-0.5 font-heading text-lg font-bold text-accent-700/70 transition-colors duration-200 group-hover:text-accent-400">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-heading text-[15px] font-semibold text-foreground-900 transition-colors duration-200 group-hover:text-accent-400">
            {article.title}
          </span>
          <span className="mt-1.5 block text-xs text-foreground-500">
            {article.categoryName} · {article.date}
          </span>
        </span>
        <i className="ri-arrow-right-up-line shrink-0 text-lg text-foreground-600 transition-colors duration-200 group-hover:text-accent-400" />
      </Link>
    );
  }

  if (variant === "feature") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group relative flex flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-100 cursor-pointer"
      >
        <div className="relative h-64 w-full overflow-hidden md:h-[340px]">
          <img
            src={article.cover}
            alt={`${article.title} - 事件复盘配图`}
            title={`${article.title} 深夜黑料复盘`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute left-4 top-4 rounded-full bg-primary-600/95 px-3 py-1 text-xs font-medium text-foreground-950">
            {article.categoryName}
          </span>
        </div>
        <div className="flex flex-1 flex-col p-5 md:p-7">
          <h3 className="font-heading text-xl font-bold leading-snug text-foreground-950 transition-colors duration-200 group-hover:text-accent-400 md:text-2xl">
            {article.title}
          </h3>
          <p className="mt-3 hidden text-sm leading-relaxed text-foreground-600 sm:block">
            {article.subtitle}
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <MetaRow article={article} />
            <span className="flex items-center gap-1 text-xs text-accent-500">
              <i className="ri-history-line text-sm" />
              {article.timeline.length} 个关键节点
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-100 transition-colors duration-300 hover:border-accent-700/60 cursor-pointer"
    >
      <div className="relative h-44 w-full overflow-hidden">
        <img
          src={article.cover}
          alt={`${article.title} - 事件复盘配图`}
          title={`${article.title} 深夜黑料复盘`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-full bg-background-50/85 px-2.5 py-1 text-[11px] font-medium text-accent-300 backdrop-blur">
          {article.categoryName}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors duration-200 group-hover:text-accent-400">
          {article.title}
        </h3>
        <p className="mt-3 line-clamp-2 flex-1 text-xs leading-relaxed text-foreground-600">
          {article.summary}
        </p>
        <div className="mt-4 border-t border-background-200/70 pt-4">
          <MetaRow article={article} />
        </div>
      </div>
    </Link>
  );
}